'use client';

import { PushDataLayerFn } from '../../../types/pushDataLayer';
import { TableBuilderValueCell } from '../types';
import { renderCalculatedCell } from './calculatedCells';
import { HotRewardCell } from './HotRewardCell';
import { TextCell } from './TextCell';
import { TotalRewardCell } from './TotalRewardCell';

interface ValueCellProps {
  cell: TableBuilderValueCell;
  heightVariant: 'COMPACT' | 'FLEXIBLE';
  showMoreLabel: string;
  showLessLabel: string;
  pushDataLayer?: PushDataLayerFn;
  index: number;
  titleTableBuilder: string;
}

/**
 * Picks the cell for a resolved Table Builder value. Every branch renders its
 * own `<td>`, so this component contributes no markup of its own — it decides,
 * and nothing else.
 */
export const ValueCell = ({
  cell,
  heightVariant,
  showMoreLabel,
  showLessLabel,
  pushDataLayer,
  index,
  titleTableBuilder,
}: ValueCellProps) => {
  // Resolved upstream, so each provider's cell is a pure formatter; which one
  // draws it is `calculatedCells.tsx`'s answer.
  if (cell.calculated) {
    return renderCalculatedCell(cell.calculated);
  }

  // Hot Reward Pick renders its own <td> (gift titles + "See the full reward"
  // link + expanded module), so we delegate entirely to it (CMS-1687). The
  // resolved payload carries everything it needs (offers, CTA, product), so no
  // extra props are threaded here. Only `resolveHotRewardCell` sets this
  // payload, so its presence already means the column is a HOT_REWARD_PICK one.
  if (cell.hotReward) {
    return (
      <HotRewardCell
        data={cell.hotReward}
        pushDataLayer={pushDataLayer}
        index={index}
        titleTableBuilder={titleTableBuilder}
      />
    );
  }

  if (cell.totalReward) {
    return (
      <TotalRewardCell
        data={cell.totalReward}
        pushDataLayer={pushDataLayer}
        index={index}
        titleTableBuilder={titleTableBuilder}
      />
    );
  }

  return (
    <TextCell
      cell={cell}
      heightVariant={heightVariant}
      showMoreLabel={showMoreLabel}
      showLessLabel={showLessLabel}
    />
  );
};
